import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import { PaginationItem } from "@mui/material";
import { ChangeEvent, SyntheticEvent, useState } from "react";
import NewsArticleDialog from "features/Dashboard/components/NewsArticleDialog/NewsArticleDialog.tsx";
import { useGetNewsArchiveQuery } from "features/Dashboard/api/dashboardApi.ts";
import type { components } from "types/declarations/api";
import * as Styles from "./NewsArchivePage.styles.ts";

const PAGE_SIZE = 10;
const FALLBACK_THUMBNAIL = "/assets/No-Photo.svg";
type NewsArchiveArticle = components["schemas"]["NewsArchiveArticle"];

const NewsArchivePage = () => {
	const [pageNumber, setPageNumber] = useState(1);
	const [selectedArticle, setSelectedArticle] = useState<NewsArchiveArticle | null>(null);
	const { data, isLoading, isFetching, isError, refetch } = useGetNewsArchiveQuery({
		pageNumber,
		pageSize: PAGE_SIZE
	});
	const articles = data?.items ?? [];
	const totalPages = data?.totalPages ?? 0;

	const handlePageChange = (_event: ChangeEvent<unknown>, nextPage: number) => {
		setPageNumber(nextPage);
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	const handleImageError = (event: SyntheticEvent<HTMLImageElement>) => {
		const image = event.currentTarget;
		if (!image.src.endsWith(FALLBACK_THUMBNAIL)) image.src = FALLBACK_THUMBNAIL;
	};

	const renderContent = () => {
		if (isLoading || isFetching) {
			return (
				<Styles.Articles data-testid="news-archive-loading-skeleton">
					{Array.from({ length: PAGE_SIZE }, (_, index) => (
						<Styles.LoadingCard
							key={index}
							variant="rectangular"
						/>
					))}
				</Styles.Articles>
			);
		}

		if (isError) {
			return (
				<Styles.StateMessage
					severity="error"
					action={
						<button
							type="button"
							onClick={() => refetch()}
						>
							Try again
						</button>
					}
				>
					We could not load the news archive.
				</Styles.StateMessage>
			);
		}

		if (articles.length === 0) {
			return <Styles.StateMessage severity="info">No news articles are available right now.</Styles.StateMessage>;
		}

		return (
			<>
				<Styles.Articles>
					{articles.map((article, index) => (
						<Styles.ArticleButton
							key={`${pageNumber}-${article.title ?? "article"}-${index}`}
							type="button"
							onClick={() => setSelectedArticle(article)}
							aria-label={`Open ${article.title ?? "news article"}`}
						>
							<Styles.Thumbnail
								src={article.thumbnailUrl || FALLBACK_THUMBNAIL}
								alt=""
								onError={handleImageError}
							/>
							<Styles.ArticleCopy>
								<Styles.ArticleTitle>{article.title}</Styles.ArticleTitle>
								<Styles.ArticleSubtitle>{article.subtitle}</Styles.ArticleSubtitle>
							</Styles.ArticleCopy>
							<Styles.Chevron aria-hidden="true">
								<ArrowForwardIosRoundedIcon fontSize="small" />
							</Styles.Chevron>
						</Styles.ArticleButton>
					))}
				</Styles.Articles>
				{totalPages > 1 && (
					<Styles.PaginationContainer>
						<Styles.PagePagination
							showFirstButton
							showLastButton
							count={totalPages}
							page={pageNumber}
							onChange={handlePageChange}
							disabled={isFetching}
							renderItem={(item) => (
								<PaginationItem
									{...item}
									slots={{ first: SkipPreviousIcon, last: SkipNextIcon }}
								/>
							)}
							shape="rounded"
							aria-label="News archive pagination"
						/>
					</Styles.PaginationContainer>
				)}
			</>
		);
	};

	return (
		<>
			<Styles.Page>
				<Styles.Header>
					<Styles.Heading>News Archive</Styles.Heading>
				</Styles.Header>
				<Styles.Content aria-busy={isLoading || isFetching}>{renderContent()}</Styles.Content>
			</Styles.Page>
			<NewsArticleDialog
				article={selectedArticle}
				onClose={() => setSelectedArticle(null)}
			/>
		</>
	);
};

export default NewsArchivePage;
